import { useState } from 'react';

import { useCheckPrinterStatusQuery } from '@api/connectOperations';
import { useGetShippingLabelQuery, usePrintLabelMutation } from '@api/shippingLabel';
import { toast } from 'sonner';
import { RDInstanceRequestStatus } from 'src/types/shared';

import { isConnectionError } from '@utils/helpers';

import { useRdInstance } from './useRdInstance';

export const usePrint = () => {
  const { instance, status, refetchInstances, error: instanceRequestError } = useRdInstance();
  const [printError, setPrintError] = useState<string | null>();

  const [printLabel, { error: printLabelError, reset: resetPrintLabel }] = usePrintLabelMutation();

  const {
    data,
    refetch,
    isLoading: isLoadingTemplateData,
  } = useGetShippingLabelQuery(undefined, { skip: !instance?.id });

  const {
    data: PrinterStatus,
    error: printerStatusError,
    refetch: refetchPrinterStatus,
  } = useCheckPrinterStatusQuery(
    { name: data?.defaultPrinterName || '', connectionId: instance?.connectionId ?? undefined },
    { skip: !data || !instance?.id },
  );

  if (!printError && data?.defaultPrinterName && (printerStatusError || (PrinterStatus && !PrinterStatus.isActive))) {
    setPrintError(data?.defaultPrinterName);
  }

  const print = async (pieceId?: number) => {
    if (!instance?.id || !data?.defaultPrinterName || !pieceId || printError) return;
    try {
      const result = await printLabel({ id: pieceId, rdConnectInstanceId: instance.id });
      if ('data' in result) {
        if (result.data?.objectData.success) {
          toast.success('Print request sent');
        } else {
          toast.error(result.data?.objectData.message || 'Something wrong with printer');
          setPrintError(data?.defaultPrinterName);
        }
      }
    } catch (error) {
      toast.error('Error while printing');
    }
  };

  const refreshPrinter = async () => {
    const response = await refetch();
    if (!response.error && response.data?.defaultPrinterName) {
      const printerStatus = await refetchPrinterStatus();
      if (printerStatus.isError || !printerStatus.data?.isActive) {
        setPrintError(data?.defaultPrinterName);
      } else {
        setPrintError(null);
      }
    }
  };

  const isLoading = [RDInstanceRequestStatus.LOADING, RDInstanceRequestStatus.RETRYING].includes(status);
  const instanceError =
    status === RDInstanceRequestStatus.ERROR || !!instanceRequestError || isConnectionError(printLabelError);

  const refreshInstannces = () => {
    resetPrintLabel();
    refetchInstances();
  };

  return {
    isConfigError: (!data?.defaultPrinterName || !data.template) && !isLoadingTemplateData,
    print,
    isLoading,
    status,
    instanceError,
    refetchInstances: refreshInstannces,
    refreshPrinter,
    printError,
    currentPrinter: data?.defaultPrinterName,
  };
};
